/**
 * Where the Home gadgets read their data (utils/gadgetData.js: one shared answer per key and project, read only while a
 * gadget that needs it is on the board, again every minute at most). Gadgets that show the same data share one hook:
 * the Quality overview (Dependencies, Tests, Security), the Traces map of the last 24 hours (Requests, API health,
 * Errors), the app configuration (Jobs, Configuration health, Open work), the data overview (Data); FinOps (Cost).
 * `projectId` null: nothing is read.
 */
import useGadgetData from '../../../utils/gadgetData';
import * as quality from '../../../services/QualityService';
import { getMap, listTraces } from '../../../services/TracesService';
import { getEndpoints } from '../../../services/ApiTesterService';
import { getAppConfig, getServiceConfig } from '../../../services/AppConfigService';
import { getOverview as getDataOverview, listBackups, listRestorePoints } from '../../../services/DataService';
import { getMyCredits } from '../../../services/CreditsService';
import { getFinOps } from '../../../services/FinOpsService';
import { getOverview as getGitOverview } from '../../../services/GitService';
import {
  getAccess, getActivity, getChanges, getComments, getNotes, getSuggestions, getUnseenHandoffs,
} from '../../../services/CollabService';
import { grepProject, listVersions } from '../../../utils/vfsClient';
import { sinceFor } from '../../../utils/changesModel';
import { lastPublish } from '../../../utils/homeGadgets/ship';

// The code search for TODO / FIXME: at most this many matches, read again every 5 minutes at most.
export const TODO_MAX = 500;
const FIVE_MIN = 5 * 60000;
const key = (name, projectId) => (projectId ? `${name}:${projectId}` : null);

export const useQualityOverview = (projectId) => useGadgetData(key('quality', projectId), () => quality.getOverview(projectId));
export const useQualityRuns = (projectId) => useGadgetData(key('quality-runs', projectId), () => quality.listRuns(projectId));
/** The Traces map of the last 24 hours (Flow keeps less: `since`/`until` say what it covers). */
export const useTracesMap = (projectId) => useGadgetData(key('traces-map-24h', projectId), () => getMap(projectId, { since: '24h' }));
export const useFailedTraces = (projectId) => useGadgetData(key('traces-failed-24h', projectId), () => listTraces(projectId, { since: '24h', status: 'failed', limit: 20 }));
export const useEndpoints = (projectId) => useGadgetData(key('api-endpoints', projectId), () => getEndpoints(projectId), { maxAgeMs: FIVE_MIN });

/**
 * The app configuration: the overview (`services` with their counts and `secretsMissing`) and the configuration of each
 * service that has variables or jobs (`details`, by service). A service's own error leaves it out of `details`.
 */
export async function loadAppConfig(projectId) {
  const overview = await getAppConfig(projectId);
  const wanted = (overview?.services || []).filter((s) => s.vars > 0 || s.jobs > 0).map((s) => s.name);
  const answers = await Promise.allSettled(wanted.map((service) => getServiceConfig(projectId, service)));
  const details = {};
  answers.forEach((answer, i) => { if (answer.status === 'fulfilled') { details[wanted[i]] = answer.value; } });
  return { overview, details, missing: wanted.filter((service) => !details[service]) };
}
export const useAppConfig = (projectId) => useGadgetData(key('app-config', projectId), () => loadAppConfig(projectId));

export const useDataOverview = (projectId) => useGadgetData(key('data', projectId), () => getDataOverview(projectId));
export const useRestorePoints = (projectId) => useGadgetData(key('restore-points', projectId), () => listRestorePoints(projectId));
export const useBackups = (projectId) => useGadgetData(key('backups', projectId), () => listBackups(projectId), { maxAgeMs: FIVE_MIN });
// The person's credits (all projects): the same answer on every project.
export const useMyCredits = (enabled = true) => useGadgetData(enabled ? 'credits:me' : null, () => getMyCredits());
// What the project costs with the admin's FinOps defaults (vfs-service's model: the Cost gadget).
export const useFinOps = (projectId) => useGadgetData(key('finops', projectId), () => getFinOps(projectId));

export const useGitOverview = (projectId) => useGadgetData(key('git', projectId), () => getGitOverview(projectId));
export const useChangesWeek = (projectId) => useGadgetData(key('changes-7d', projectId), () => getChanges(projectId, { since: sinceFor('7d'), limit: 500 }));
/**
 * The last Publish (the newest version saved by one, `listVersions`) and what changed since (`GET /collab/changes?since=`):
 * `{ publish, changes }`; never published: `publish` null and `changes` null (nothing to compare with).
 */
export async function loadRelease(projectId) {
  const publish = lastPublish(await listVersions(projectId));
  if (!publish) { return { publish: null, changes: null }; }
  const answer = await getChanges(projectId, { since: publish.createdAt, limit: 500 });
  return { publish, changes: answer?.changes || [] };
}
export const useRelease = (projectId) => useGadgetData(key('release', projectId), () => loadRelease(projectId));
export const useActivity =(projectId) => useGadgetData(key('activity', projectId), () => getActivity(projectId, { limit: 300 }));
export const useTodo = (projectId) => useGadgetData(
  key('todo', projectId),
  () => grepProject(projectId, { pattern: '\\b(TODO|FIXME)\\b', glob: 'services/**', maxResults: TODO_MAX }),
  { maxAgeMs: FIVE_MIN },
);

export const useComments = (projectId) => useGadgetData(key('comments', projectId), () => getComments(projectId));
export const useSuggestions = (projectId) => useGadgetData(key('suggestions', projectId), () => getSuggestions(projectId));
export const useUnseenHandoffs = (projectId) => useGadgetData(key('handoffs-unseen', projectId), () => getUnseenHandoffs(projectId));
export const useAccess = (projectId) => useGadgetData(key('access', projectId), () => getAccess(projectId), { maxAgeMs: FIVE_MIN });
export const useNotes = (projectId) => useGadgetData(key('notes', projectId), () => getNotes(projectId, { limit: 200 }));
